import 'chartjs-adapter-date-fns';

import type { Chart, ChartData,ChartOptions } from 'chart.js';
import React, { useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { css } from 'styled-components';

import { CenterFlex,VFlex } from '@/components/base';
import useColor from '@/hooks/domain/useColor';
import { chartColors } from '@/styles/colors/chartColors';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { getTimezoneDayjs } from '@/utils/time';

import type { ProductRankingItem } from '../types';

interface RankingDataPoint {
  x: number; // timestamp
  y: number; // ranking position
}

export const RankingLineChart = ({
  data,
  selectedDate,
}: {
  data: ProductRankingItem[];
  selectedDate: string;
}) => {
  const color = useColor();
  const chartRef = useRef<Chart>(null);

  // Process data for Chart.js
  const chartData: ChartData<'line'> = useMemo(() => {
    if (!data) {
      return { datasets: [] };
    }

    // Find the most recent date
    const mostRecentDate = data.reduce((maxDate, product) => {
      const currentDate = new Date(product.crawlDate);
      return currentDate > new Date(maxDate) ? product.crawlDate : maxDate;
    }, data[0].crawlDate);

    // Get products from the most recent date and sort by ranking
    const recentProducts = data
      .filter((product) => product.crawlDate === mostRecentDate)
      .sort((a, b) => a.currentRank - b.currentRank)
      .slice(0, 10); // Get top 10 products

    // Get the product names of top 10 products
    const top10ProductNames = new Set(recentProducts.map((p) => p.productName));

    // Group all historical data for these top 10 products
    const productMap = new Map<string, ProductRankingItem[]>();

    data.forEach((product) => {
      const key = product.productName;
      if (top10ProductNames.has(key)) {
        if (!productMap.has(key)) {
          productMap.set(key, []);
        }
        productMap.get(key)!.push(product);
      }
    });

    // Create datasets for each of the top 10 products in ranking order
    const datasets = recentProducts
      .map((recentProduct) => productMap.get(recentProduct.productName))
      .filter((products): products is ProductRankingItem[] => Boolean(products))
      .map((products, index) => {
        const productName = products[0].productName;
        // Create data points from ranking history
        const dataPoints: RankingDataPoint[] = [];

        products.forEach((product) => {
          // Add current ranking data point
          const currentDate = product.crawlDate;
          if (currentDate) {
            dataPoints.push({
              x: getTimezoneDayjs(currentDate).valueOf(),
              y: product.currentRank,
            });
          }
        });

        // Sort by date and remove duplicates
        const sortedUniquePoints = dataPoints
          .sort((a, b) => a.x - b.x)
          .filter(
            (point, index, arr) => index === 0 || point.x !== arr[index - 1].x,
          );

        const selectedTimestamp = getTimezoneDayjs(selectedDate).valueOf();

        // Generate color for this product line
        const lineColor = chartColors[index];
        const lineColorArray = sortedUniquePoints.map((point) => {
          if (
            selectedTimestamp &&
            Math.abs(point.x - selectedTimestamp) < 24 * 60 * 60 * 1000
          ) {
            return lineColor;
          }
          return getAlphaColor(lineColor, 0.3);
        });

        // Calculate point radius for each data point based on selectedDate
        const pointRadiusArray = sortedUniquePoints.map((point) => {
          // Check if this point's date matches the selected date
          if (
            selectedTimestamp &&
            Math.abs(point.x - selectedTimestamp) < 24 * 60 * 60 * 1000
          ) {
            return 10; // Highlight selected date point
          }
          return 6; // Default size
        });

        return {
          label: productName,
          data: sortedUniquePoints,
          borderColor: getAlphaColor(lineColor, 0.5),
          backgroundColor: lineColorArray,
          borderWidth: 2,
          fill: false,
          tension: 0.1,
          pointRadius: pointRadiusArray,
          pointHoverRadius: 10,
        };
      });

    return { datasets };
  }, [data, selectedDate]);

  const chartOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'point',
        intersect: true,
      },
      animation: {
        duration: 0,
      },
      layout: {
        padding: {
          right: 100,
        },
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'day',
            displayFormats: {
              day: 'MM/dd',
            },
          },
          grid: {
            color: color.gray300,
          },
          ticks: {
            color: color.gray600,
          },
        },
        y: {
          reverse: true, // Lower ranking numbers (1, 2, 3) should be at the top
          min: 0.5,
          max: 10,
          title: {
            display: true,
            text: 'Rank',
            color: color.gray700,
          },
          grid: {
            color: color.gray300,
          },
          ticks: {
            color: color.gray600,
            stepSize: 1,
            callback: function (value) {
              // Only show integer values
              return Number.isInteger(value) ? value : '';
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: color.gray100,
          titleColor: color.gray800,
          bodyColor: color.gray700,
          borderColor: color.gray300,
          borderWidth: 1,
          callbacks: {
            title: (context) => {
              return getTimezoneDayjs(context[0].parsed.x).format('MM/DD');
            },
            label: (context) => {
              return `${context.dataset.label}: ${context.parsed.y}위`;
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
      elements: {
        point: {
          hoverBorderWidth: 12,
        },
      },
    }),
    [color],
  );

  return (
    <VFlex g={16} f={1}>
      <CenterFlex
        css={css`
          height: 500px;
          width: 100%;
          position: relative;
        `}
      >
        <Line ref={chartRef as any} data={chartData} options={chartOptions} />
      </CenterFlex>
    </VFlex>
  );
};
